/**
 * sliceResolver.js — Pure utility for computing bounded code slices.
 *
 * Called when user clicks an outline item, traceability mapping, or tree node.
 * Returns bounded source (COBOL) + target (Java) slices for the split editor.
 *
 * Slice state shape:
 *   { active, source: {filePath, startLine, endLine, content, anchorName},
 *             target: {filePath, startLine, endLine, content, anchorName} }
 *
 * Persisted per jobId in localStorage (ws_slice_{jobId}).
 */

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const METHOD_SIG_RE = /^\s*(public|private|protected|static|\s)+[\w<>\[\],\s]+\s+(\w+)\s*\(/
const COBOL_PARA_RE = /^(\s{0,6})([A-Z0-9][\w-]*)\.\s*$/

/**
 * Find Java method boundaries around a given anchor line.
 * Scans backward for method signature, forward counting braces to find the end.
 * Returns {startLine, endLine, anchorName} (1-based lines) or null.
 */
function findJavaMethodBounds(content, anchorLine) {
  const lines = content.split('\n')
  if (anchorLine < 1 || anchorLine > lines.length) return null

  // Scan backward from anchorLine to find method signature
  let methodLine = -1
  let methodName = ''
  for (let i = anchorLine - 1; i >= 0; i--) {
    const m = lines[i].match(METHOD_SIG_RE)
    if (m) {
      methodLine = i
      methodName = m[2]
      break
    }
    // Stop if we hit a class declaration (we've gone too far)
    if (/^\s*(public|private|protected)?\s*(abstract\s+)?(class|interface|enum)\s/.test(lines[i])) break
  }

  if (methodLine === -1) return null

  // Include any JavaDoc/annotations above the method
  let startLine = methodLine
  for (let i = methodLine - 1; i >= 0; i--) {
    const trimmed = lines[i].trim()
    if (trimmed.startsWith('@') || trimmed.startsWith('*') || trimmed.startsWith('/**') || trimmed === '*/') {
      startLine = i
    } else if (trimmed === '' && i === methodLine - 1) {
      // skip one blank line above
    } else {
      break
    }
  }

  // Scan forward counting braces to find method end
  let braceCount = 0
  let foundOpen = false
  let endLine = lines.length - 1
  for (let i = methodLine; i < lines.length; i++) {
    for (const ch of lines[i]) {
      if (ch === '{') { braceCount++; foundOpen = true }
      if (ch === '}') braceCount--
    }
    if (foundOpen && braceCount <= 0) {
      endLine = i
      break
    }
  }

  return {
    startLine: startLine + 1,  // 1-based
    endLine: endLine + 1,      // 1-based
    anchorName: methodName,
  }
}

/**
 * Find the first Java method in file content.
 */
function findFirstMethod(content) {
  const lines = content.split('\n')
  for (let i = 0; i < lines.length; i++) {
    if (METHOD_SIG_RE.test(lines[i])) {
      return findJavaMethodBounds(content, i + 1)
    }
  }
  return null
}

/**
 * Find COBOL paragraph boundaries by name.
 * Checks analysisData.paragraphs first (has line_start/line_end).
 * Falls back to text search in sourceContent.
 */
function findCobolParagraphBounds(sourceContent, paragraphName, analysisData) {
  if (!paragraphName || !sourceContent) return null

  const upperName = paragraphName.toUpperCase().replace(/\s+/g, '-')

  // Strategy 1: Use analysisData.paragraphs if available
  if (analysisData?.paragraphs?.length > 0) {
    const match = analysisData.paragraphs.find(p => {
      const pName = (p.name || '').toUpperCase().replace(/\s+/g, '-')
      return pName === upperName || pName.includes(upperName) || upperName.includes(pName)
    })
    if (match && match.line_start) {
      return {
        startLine: match.line_start,
        endLine: match.line_end || match.line_start + 20,
        anchorName: match.name || paragraphName,
      }
    }
  }

  // Strategy 2: Text search in source content
  const lines = sourceContent.split('\n')
  let startLine = -1
  let foundName = paragraphName

  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(COBOL_PARA_RE)
    if (m) {
      const name = m[2].toUpperCase()
      if (name === upperName || name.includes(upperName) || upperName.includes(name)) {
        startLine = i
        foundName = m[2]
        break
      }
    }
  }

  if (startLine === -1) return null

  // Find end: next paragraph declaration or end of content
  let endLine = lines.length - 1
  for (let i = startLine + 1; i < lines.length; i++) {
    const m = lines[i].match(COBOL_PARA_RE)
    if (m) {
      endLine = i - 1
      break
    }
  }

  return {
    startLine: startLine + 1,
    endLine: endLine + 1,
    anchorName: foundName,
  }
}

/**
 * Convert a Java method name to likely COBOL paragraph name.
 * e.g., "parseCustRecord" → "PARSE-CUST-RECORD"
 */
function javaNameToCobol(javaName) {
  if (!javaName) return ''
  // Split camelCase into parts
  return javaName
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toUpperCase()
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve a code slice from available context.
 *
 * @param {Object} opts
 * @returns {Object|null} sliceState or null if insufficient data
 */
export function resolveSlice({
  targetPath,
  targetContent,
  outlineItem = null,
  traceabilityMapping = null,
  traceabilityData = null,
  sourceContent = '',
  sourcePath = null,
  analysisData = null,
}) {
  if (!targetPath || !targetContent) return null

  // --- Resolve Java target slice ---
  let targetSlice = null

  if (outlineItem && outlineItem.type === 'method' && outlineItem.line) {
    targetSlice = findJavaMethodBounds(targetContent, outlineItem.line)
  } else if (traceabilityMapping && traceabilityMapping.line) {
    targetSlice = findJavaMethodBounds(targetContent, traceabilityMapping.line)
  }

  if (!targetSlice) {
    targetSlice = findFirstMethod(targetContent)
  }

  if (!targetSlice) {
    // Fallback: full file
    const lineCount = targetContent.split('\n').length
    targetSlice = { startLine: 1, endLine: lineCount, anchorName: targetPath.split('/').pop().replace(/\.\w+$/, '') }
  }

  // Extract target slice content
  const targetLines = targetContent.split('\n')
  const targetSliceContent = targetLines.slice(targetSlice.startLine - 1, targetSlice.endLine).join('\n')

  // --- Resolve COBOL source slice ---
  let sourceSlice = null
  let cobolParaName = null

  // Determine COBOL paragraph name from available data
  if (traceabilityMapping) {
    cobolParaName = traceabilityMapping.source || traceabilityMapping.cobol_name || traceabilityMapping.cobol_paragraph
  }
  if (!cobolParaName && targetSlice.anchorName && traceabilityData?.mappings) {
    // Reverse lookup: find COBOL paragraph from Java method name
    const javaMethodName = targetSlice.anchorName
    const mapping = traceabilityData.mappings.find(m => {
      const tgt = m.target || m.java_method || ''
      return tgt === javaMethodName || tgt.toLowerCase() === javaMethodName.toLowerCase()
    })
    if (mapping) {
      cobolParaName = mapping.source || mapping.cobol_name || mapping.cobol_paragraph
    }
  }
  if (!cobolParaName && targetSlice.anchorName) {
    // Last resort: derive COBOL name from Java method name
    cobolParaName = javaNameToCobol(targetSlice.anchorName)
  }

  if (sourceContent && cobolParaName) {
    sourceSlice = findCobolParagraphBounds(sourceContent, cobolParaName, analysisData)
  }

  // If no specific paragraph found, show full COBOL source
  let sourceSliceContent = sourceContent
  let sourceResult = {
    filePath: sourcePath,
    startLine: 1,
    endLine: sourceContent ? sourceContent.split('\n').length : 0,
    content: sourceContent,
    anchorName: sourcePath ? sourcePath.split('/').pop() : null,
  }

  if (sourceSlice && sourceContent) {
    const sourceLines = sourceContent.split('\n')
    sourceSliceContent = sourceLines.slice(sourceSlice.startLine - 1, sourceSlice.endLine).join('\n')
    sourceResult = {
      filePath: sourcePath,
      startLine: sourceSlice.startLine,
      endLine: sourceSlice.endLine,
      content: sourceSliceContent,
      anchorName: sourceSlice.anchorName,
    }
  }

  return {
    active: true,
    source: sourceResult,
    target: {
      filePath: targetPath,
      startLine: targetSlice.startLine,
      endLine: targetSlice.endLine,
      content: targetSliceContent,
      anchorName: targetSlice.anchorName,
    },
  }
}

/**
 * Merge an edited slice back into the full file content.
 *
 * @param {string} fullContent - Full file content
 * @param {number} sliceStartLine - 1-based start line of slice
 * @param {number} sliceEndLine - 1-based end line of slice
 * @param {string} editedSlice - New content for the slice
 * @returns {string} Updated full content
 */
export function applySliceEdit(fullContent, sliceStartLine, sliceEndLine, editedSlice) {
  const lines = fullContent.split('\n')
  const editedLines = editedSlice.split('\n')
  const before = lines.slice(0, sliceStartLine - 1)
  const after = lines.slice(sliceEndLine)
  return [...before, ...editedLines, ...after].join('\n')
}
